import React from 'react';
import R from 'ramda';

import { Button, Link } from '../../common/components/core';
import EmailInput from './EmailInput';
import PasswordInput from './PasswordInput';
import LoggedoutActions from '../../main/actions/LoggedoutActions';
import LoggedoutStore from '../../main/stores/LoggedoutStore';
import { emailValid, inputTooLong } from './ScreenSignupValidation';

import { track } from '../../common/support/mixpanel';
import { getCookie } from '../../main/support/utils';
import * as queryString from 'query-string';

class ScreenLogin extends React.Component {
  constructor(props) {
    super(props);
    this.textInput = null;
    this.setTextInputRef = element => {
      this.textInput = element;
    };
    this.state = {
      email: null,
      password: null,
      errors: LoggedoutStore.getErrors(),
      loadingSignIn: false,
      gitHubLoadingSignIn: false,
      emailChecked: false,
      emailValid: false,
      passwordChecked: false,
      passwordValid: false,
      errorFootnotes: {
        email: null,
        password: null
      }
    };
  }

  _assignForm = form => {
    this.form = form;
  };

  componentWillMount() {
    window.addEventListener('keypress', this._handleEnterKeyPress);
    LoggedoutStore.addChangeListener(this._onChange);
  }

  componentWillUnmount() {
    window.removeEventListener('keypress', this._handleEnterKeyPress);
    LoggedoutStore.removeChangeListener(this._onChange);
    if (this.form) {
      this.form.removeEventListener('invalid', this._preventDefault, true);
    }
  }

  componentDidMount() {
    LoggedoutActions.clearErrors();
    if (this.form) {
      this.form.addEventListener('invalid', this._preventDefault, true);
    }
  }

  _handleEnterKeyPress = (event: Object) => {
    if (event.keyCode === 13) {
      this._login();
    }
  };

  _onChange = () => {
    const errors = LoggedoutStore.getErrors();
    if (errors.login_attempts) {
      return this.props.history.push('/reset');
    }
    let loadingSignIn = this.state.loadingSignIn;
    if (errors && Object.keys(errors).length !== 0) {
      loadingSignIn = false;
    }
    this.setState(
      { errors, loadingSignIn },
      this._checkEmailVerificationStatus
    );
  };

  _checkEmailVerificationStatus() {
    if (this.state.errors.email_verification) {
      localStorage.setItem('accept_invite_user_email', this.state.email);
      this.props.history.push('/accept-email-invite');
    }
  }

  _handleEmailChange = ev => {
    this.setState({ email: ev.value });
    this._validateEmail(ev.value);
    LoggedoutActions.clearErrors();
  };

  _handlePasswordChange = ev => {
    this.setState({ password: ev.value });
    this._validatePassword(ev.value);
    LoggedoutActions.clearErrors();
  };

  _preventDefault = ev => {
    ev.preventDefault();
  };

  _gitHubLoadingSignIn = () => {
    this.setState({ gitHubLoadingSignIn: true });
  };

  _mixpanelTracking = (pageName: string) => {
    track(`Sign In button clicked on ${pageName}`, {
      bmp: getCookie('bmp')
    });
  };

  _login = () => {
    this.setState({
      loadingSignIn: true,
      emailChecked: true,
      passwordChecked: true
    });
    let email = this.state.email;
    let password = this.state.password;

    // Handle Ugly Safari Autofill -- https://github.com/facebook/react/issues/1159
    if (!email) {
      email = this.setTextInputRef.value;
    }

    if (!password) {
      password = this.setTextInputRef.value;
    }

    if (!this.state.email && !this.state.password) {
      const currentErrors = { ...this.state.errorFootnotes };
      currentErrors.email =
        'Please specify a valid email address e.g., myname@email.com.';
      currentErrors.password = 'Please specify a password.';
      this.setState({
        errorFootnotes: currentErrors,
        loadingSignIn: false
      });
    } else {
      this._mixpanelTracking('Login Page');

      const query = queryString.parse(this.props.location.search);

      LoggedoutActions.login(
        email,
        password,
        R.pathOr(false, ['next_path'], query)
      );
    }
  };

  _validateEmail = input => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.email = !emailValid(input)
      ? 'Please specify a valid email address e.g., myname@email.com.'
      : inputTooLong(input)
      ? 'Please specify a shorter email.'
      : '';
    this.setState({
      errorFootnotes: currentErrors,
      emailValid: !!emailValid(input),
      emailChecked: true
    });
  };

  _validatePassword = input => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.password = !input ? 'Please specify a password.' : '';
    this.setState({
      errorFootnotes: currentErrors,
      passwordChecked: true,
      passwordValid: !!input
    });
  };

  _renderErrors = key => {
    return this.state.errorFootnotes[key]
      ? this.state.errorFootnotes[key]
      : this.state.errors[key];
  };

  render() {
    return (
      <div className="loggedout--container">
        <img src="/static/images/branch-logos/branch-logo_dark.svg" />
        <div className="login-container">
          <div className="auth-header">
            <h1 className="create-account h_fw-700">Sign in</h1>
            <div className="signin-link-container">
              <Link
                to="/"
                className="signin-link"
                i13nModel={{ label: 'to Sign up page' }}
                testid="link-to-signup"
              >
                <span className="h_fw-400">New user?</span>
                Create an Account
              </Link>
            </div>
          </div>
          <form
            role="form"
            id="form_login"
            className="sign-up-form-1 login-form Bizible-Exclude"
            ref={this._assignForm}
            onSubmit={this._preventDefault}
          >
            <div className="modal-body">
              <div className="sign-up-container">
                <div className="sign-up-1">
                  <EmailInput
                    onChange={this._handleEmailChange}
                    value={this.state.email}
                    name="email"
                    ref={this.setTextInputRef}
                    placeholder="Work Email"
                    footnote={
                      this._renderErrors('email') ||
                      this._renderErrors('account_blocked')
                    }
                    i13nLabel="Work Email"
                    testid="work-email-input"
                    label="Work Email"
                    className="login-input"
                    hasError={
                      this._renderErrors('email') ||
                      this._renderErrors('account_blocked')
                    }
                    inputChecked={this.state.emailChecked}
                    inputValid={this.state.emailValid}
                  />
                  <PasswordInput
                    label="Password"
                    onChange={this._handlePasswordChange}
                    value={this.state.password}
                    name="password"
                    ref={this.setTextInputRef}
                    testid="password-input"
                    i13nLabel="Password input"
                    hasError={this._renderErrors('password')}
                    footnote={this._renderErrors('password')}
                    inputValid={this.state.passwordValid}
                    inputChecked={this.state.passwordChecked}
                    className="login-input"
                    displayShowHide={true}
                    pasteDisabled={true}
                    autoComplete="current-password"
                  />
                </div>
              </div>
              <div className="login-signup h_mt-10">
                <Link to="/reset" testid="forgot-password">
                  Forgot password?
                </Link>
              </div>
            </div>

            <div className="loggedout--footer signin">
              <div className="loggedout--sign-in-options">
                <Button
                  onClick={this._login}
                  loading={this.state.loadingSignIn}
                  className="btn-signup"
                  testid="sign-in"
                  nativeButtonType="submit"
                >
                  Sign In
                </Button>
              </div>
              <img
                src="/static/images/signin_logInWith_footer.svg"
                className="signin-github-footer"
              />
              <Button
                href={`https://github.com/login/oauth/authorize?scope=user:email&client_id=${GITHUB_CLIENT_ID}&state=${window._oauthState}`}
                newTab={false}
                onClick={this._gitHubLoadingSignIn}
                loading={this.state.gitHubLoadingSignIn}
                type="secondary"
                className="btn-signup btn-icon-with-text github-btn"
                i13nModel={{ label: 'Sign in with GitHub' }}
                testid="sign-in__github"
                nativeButtonType="button"
              >
                <i className="fa fa-github fa-fx github-icon" />
                GitHub
              </Button>
            </div>
            <div className="signup-footer">
              <span>Questions? We are here to </span>
              <Link href="https://support.branch.io" testid="help-support-link">
                help
              </Link>
              <span>.</span>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default ScreenLogin;
